import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { blogArticleSlug } from "@/mocks/blogContent";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const categoryIcon = (name: string) => {
    const map: Record<string, string> = {
      Shoes: "ri-footprint-line",
      "Hoodies/Sweaters": "ri-t-shirt-line",
      "T-Shirts": "ri-shirt-line",
      Jackets: "ri-honour-line",
      "Pants/Shorts": "ri-pantone-line",
      Headwear: "ri-customer-service-2-line",
      Sets: "ri-stack-line",
      "Underwear/Underpants": "ri-checkbox-blank-circle-line",
      Jersey: "ri-archive-line",
      Accessories: "ri-glasses-line",
      Bags: "ri-handbag-line",
      Belts: "ri-link",
      Perfumes: "ri-drop-line",
      OtherStuff: "ri-more-line",
    };
    return map[name] || "ri-store-line";
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.06)]"
          : "bg-transparent [text-shadow:_0_1px_4px_rgba(0,0,0,0.35)]"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className={`text-lg md:text-xl font-heading font-semibold tracking-tight whitespace-nowrap transition-colors duration-300 ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            mulebuyspreadsheet<span className="text-accent-500">.</span>today
          </Link>

          {/* Desktop Nav Items */}
          <div className="hidden md:flex items-center gap-2.5">
            {/* Home */}
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-home-4-line"></i>
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to={`/blog/${blogArticleSlug}`}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-book-open-line"></i>
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "border-foreground-300 text-foreground-800 hover:border-foreground-500"
                    : "border-white/60 text-white hover:border-white hover:bg-white/10"
                }`}
              >
                <span>Browse Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-60 bg-white rounded-xl border border-background-200/70 py-2 shadow-[0_4px_24px_rgba(0,0,0,0.08)] max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className={`${categoryIcon(cat.name)} text-base text-foreground-400`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTAs */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/15 text-white border border-white/30 hover:bg-white/25"
              }`}
            >
              Mulebuy Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-black/20">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white rounded-2xl border border-background-200/70 shadow-[0_8px_32px_rgba(0,0,0,0.1)] mt-2 mb-4 overflow-hidden">
            {/* Quick Nav */}
            <div className="flex border-b border-background-200/70">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-50 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Home
              </Link>
              <Link
                to={`/blog/${blogArticleSlug}`}
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-50 border-l border-background-200/70 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Shopping Guide
              </Link>
            </div>

            {/* Categories Accordion */}
            <div className="p-4">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full text-sm font-medium text-foreground-900 py-2 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line"></i>
                  Browse Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-lg transition-colors whitespace-nowrap"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <i className={`${categoryIcon(cat.name)} text-sm text-foreground-400 flex-shrink-0`}></i>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="px-4 pb-4 flex flex-col gap-2.5">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-4 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Mulebuy Spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-4 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center rounded-full bg-black/20">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}